/**
 * Alert rules — a source-agnostic condition engine.
 *
 * The platform already had a legacy alert system, but its rules are welded to one data shape and
 * evaluate a flat list of conditions. This engine is deliberately independent of any single feed:
 * a source declares which fields it exposes, and a rule is a nested tree of AND/OR groups over
 * those fields. The same rule builder therefore drives Elite Pulse opportunities, the live tape,
 * and the 30-second stable fetch without any of them knowing about the others.
 *
 * Firing is de-duplicated by (rule, record) so a matching row alerts once, not on every poll — the
 * thing that makes a 30-second loop tolerable rather than a klaxon.
 */

export type AlertFieldType = 'number' | 'text' | 'enum';

export interface AlertFieldDef {
  key: string;
  label: string;
  type: AlertFieldType;
  options?: string[];
}

/** A feed that rules can be written against. */
export interface AlertSourceDef {
  id: string;
  label: string;
  fields: AlertFieldDef[];
  /** Stable identity for a record, so the same opportunity is not alerted twice. */
  recordKey: (record: Record<string, unknown>) => string;
  /** One-line human description of a record, used in the notification body. */
  describe: (record: Record<string, unknown>) => string;
  /**
   * Contract identity for the record, when it has one, so an alert can be opened straight into
   * the historical view. Sources whose records are not a single contract return null.
   */
  historicalParams?: (record: Record<string, unknown>) => {
    underlying: string;
    strike: number | string;
    expiry?: string;
    optionType?: 'CALL' | 'PUT';
  } | null;
}

export type AlertOp =
  | 'gt' | 'gte' | 'lt' | 'lte' | 'eq' | 'ne'
  | 'contains' | 'not_contains'
  | 'is_empty' | 'not_empty'
  | 'abs_gte' | 'abs_lte';

export const ALERT_OP_LABELS: Record<AlertOp, string> = {
  gt: '>',
  gte: '≥',
  lt: '<',
  lte: '≤',
  eq: 'is',
  ne: 'is not',
  contains: 'contains',
  not_contains: 'does not contain',
  is_empty: 'is empty',
  not_empty: 'is not empty',
  // The legacy Delta Premium presets compared magnitudes, so direction of flow did not matter.
  abs_gte: '|x| ≥',
  abs_lte: '|x| ≤',
};

export function opsForFieldType(type: AlertFieldType): AlertOp[] {
  if (type === 'number') return ['gte', 'lte', 'gt', 'lt', 'eq', 'ne', 'abs_gte', 'abs_lte', 'not_empty', 'is_empty'];
  if (type === 'enum') return ['eq', 'ne', 'not_empty', 'is_empty'];
  return ['contains', 'not_contains', 'eq', 'ne', 'not_empty', 'is_empty'];
}

/** Operators that ignore the value input entirely. */
export function opNeedsValue(op: AlertOp): boolean {
  return op !== 'is_empty' && op !== 'not_empty';
}

export interface AlertCondition {
  kind: 'condition';
  id: string;
  field: string;
  op: AlertOp;
  value: string;
}

export interface AlertGroup {
  kind: 'group';
  id: string;
  logic: 'AND' | 'OR';
  children: AlertNode[];
}

export type AlertNode = AlertCondition | AlertGroup;

export interface AlertActions {
  popup: boolean;
  sound: boolean;
  /** OS-level notification; requires permission, requested when the rule is enabled. */
  browser: boolean;
  soundFile?: string;
}

export interface AlertRule {
  id: string;
  name: string;
  sourceId: string;
  enabled: boolean;
  root: AlertGroup;
  actions: AlertActions;
  /** Never fire the same rule more than once per this many seconds, across all records. */
  throttleSec: number;
  createdAt: number;
}

// A newly created rule is silent by default: it shows its in-app toast (the
// point of a rule the user built) but plays no sound and sends no OS
// notification unless the user turns those on for the rule.
export const DEFAULT_ACTIONS: AlertActions = {
  popup: true,
  sound: false,
  browser: false,
  soundFile: '/bell-notification-337658.mp3',
};

let idSeq = 0;
export function newId(prefix: string): string {
  idSeq += 1;
  return `${prefix}_${Date.now().toString(36)}_${idSeq}`;
}

export function emptyGroup(logic: 'AND' | 'OR' = 'AND'): AlertGroup {
  return { kind: 'group', id: newId('g'), logic, children: [] };
}

export function newCondition(field: string, type: AlertFieldType): AlertCondition {
  return {
    kind: 'condition',
    id: newId('c'),
    field,
    op: type === 'number' ? 'gte' : type === 'enum' ? 'eq' : 'contains',
    value: '',
  };
}

export function newRule(sourceId: string, field: AlertFieldDef | undefined, name: string): AlertRule {
  const root = emptyGroup('AND');
  if (field) root.children.push(newCondition(field.key, field.type));
  return {
    id: newId('r'),
    name,
    sourceId,
    enabled: true,
    root,
    actions: { ...DEFAULT_ACTIONS },
    throttleSec: 30,
    createdAt: Date.now(),
  };
}

// ── evaluation ────────────────────────────────────────────────────────────────

function toNumber(v: unknown): number | null {
  if (typeof v === 'number') return Number.isFinite(v) ? v : null;
  const n = parseFloat(String(v ?? '').replace(/[$,%\s,]/g, ''));
  return Number.isFinite(n) ? n : null;
}

function isEmptyValue(v: unknown): boolean {
  return v == null || String(v).trim() === '';
}

export function evaluateCondition(
  record: Record<string, unknown>,
  cond: AlertCondition,
  fields: Record<string, AlertFieldDef>,
): boolean {
  const cell = record[cond.field];
  if (cond.op === 'is_empty') return isEmptyValue(cell);
  if (cond.op === 'not_empty') return !isEmptyValue(cell);

  const wanted = cond.value.trim();
  // An unfinished condition must not fire an alert, so treat it as unmet rather than as "any".
  if (!wanted) return false;

  const def = fields[cond.field];
  if (def?.type === 'number') {
    const target = toNumber(wanted);
    const actual = toNumber(cell);
    if (target === null || actual === null) return false;
    switch (cond.op) {
      case 'gt': return actual > target;
      case 'gte': return actual >= target;
      case 'lt': return actual < target;
      case 'lte': return actual <= target;
      case 'eq': return actual === target;
      case 'ne': return actual !== target;
      case 'abs_gte': return Math.abs(actual) >= Math.abs(target);
      case 'abs_lte': return Math.abs(actual) <= Math.abs(target);
      default: return false;
    }
  }

  const actualStr = String(cell ?? '').toLowerCase();
  const wantedStr = wanted.toLowerCase();
  switch (cond.op) {
    case 'contains': return actualStr.includes(wantedStr);
    case 'not_contains': return !actualStr.includes(wantedStr);
    case 'eq': return actualStr === wantedStr;
    case 'ne': return actualStr !== wantedStr;
    default: return false;
  }
}

export function evaluateNode(
  record: Record<string, unknown>,
  node: AlertNode,
  fields: Record<string, AlertFieldDef>,
): boolean {
  if (node.kind === 'condition') return evaluateCondition(record, node, fields);
  const children = node.children;
  // An empty group matches nothing: a rule with no conditions should never alert on everything.
  if (!children.length) return false;
  return node.logic === 'AND'
    ? children.every((child) => evaluateNode(record, child, fields))
    : children.some((child) => evaluateNode(record, child, fields));
}

export function ruleMatches(
  record: Record<string, unknown>,
  rule: AlertRule,
  fields: Record<string, AlertFieldDef>,
): boolean {
  return evaluateNode(record, rule.root, fields);
}

/** Human-readable rendering of a rule, for the panel summary and notification body. */
export function describeNode(node: AlertNode, fields: Record<string, AlertFieldDef>): string {
  if (node.kind === 'condition') {
    const label = fields[node.field]?.label ?? node.field;
    if (!opNeedsValue(node.op)) return `${label} ${ALERT_OP_LABELS[node.op]}`;
    return `${label} ${ALERT_OP_LABELS[node.op]} ${node.value || '…'}`;
  }
  if (!node.children.length) return '(no conditions)';
  const inner = node.children.map((c) => describeNode(c, fields)).join(` ${node.logic} `);
  return node.children.length > 1 ? `(${inner})` : inner;
}

// ── tree editing helpers (immutable) ──────────────────────────────────────────

export function updateNode(root: AlertGroup, id: string, patch: Partial<AlertCondition> & Partial<AlertGroup>): AlertGroup {
  const walk = (node: AlertNode): AlertNode => {
    if (node.id === id) return { ...node, ...patch } as AlertNode;
    if (node.kind === 'group') return { ...node, children: node.children.map(walk) };
    return node;
  };
  return walk(root) as AlertGroup;
}

export function addToGroup(root: AlertGroup, groupId: string, child: AlertNode): AlertGroup {
  const walk = (node: AlertNode): AlertNode => {
    if (node.kind !== 'group') return node;
    if (node.id === groupId) return { ...node, children: [...node.children, child] };
    return { ...node, children: node.children.map(walk) };
  };
  return walk(root) as AlertGroup;
}

export function removeNode(root: AlertGroup, id: string): AlertGroup {
  const walk = (node: AlertGroup): AlertGroup => ({
    ...node,
    children: node.children
      .filter((c) => c.id !== id)
      .map((c) => (c.kind === 'group' ? walk(c) : c)),
  });
  return walk(root);
}

export function countConditions(node: AlertNode): number {
  if (node.kind === 'condition') return 1;
  return node.children.reduce((n, c) => n + countConditions(c), 0);
}


/**
 * The conditions that actually matched, rendered with the record's own values — "Premium 2,150,000
 * ≥ 1,000,000" rather than a bare rule name. This is what makes a notification worth reading: it
 * says why it fired, not merely that it did.
 */
export function explainMatch(
  record: Record<string, unknown>,
  node: AlertNode,
  fields: Record<string, AlertFieldDef>,
  out: string[] = [],
): string[] {
  if (node.kind === 'group') {
    for (const child of node.children) {
      // Under OR only the satisfied branches are worth reporting; under AND all of them are.
      if (node.logic === 'AND' || evaluateNode(record, child, fields)) {
        explainMatch(record, child, fields, out);
      }
    }
    return out;
  }
  if (!evaluateCondition(record, node, fields)) return out;
  const label = fields[node.field]?.label ?? node.field;
  const raw = record[node.field];
  const shown = raw == null || String(raw).trim() === '' ? '—' : String(raw);
  const pretty = /^-?\d+(\.\d+)?$/.test(shown) ? Number(shown).toLocaleString() : shown;
  out.push(
    opNeedsValue(node.op)
      ? `${label} ${pretty} ${ALERT_OP_LABELS[node.op]} ${node.value}`
      : `${label} ${ALERT_OP_LABELS[node.op]}`,
  );
  return out;
}
